import React, { useCallback, useEffect, useRef, useState } from "react"

import styled from "@emotion/styled"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material"

import { COLORS } from "../../../../utils"
import EditableSection from "./EditableSection"
import SectionPreview from "./SectionPreview"
import { PRODUCTION_STEPS_SPACINGS } from "../../../../utils/constant"
import Steps from "../steps/Steps"
import { computeProductionStepsRecipeOnFieldChange, computeRecipeData } from "../../../../utils/recipes"
import SectionOutput from "./sectionOutput/SectionOutput"

export const COMPONENT_NAME = "SECTIONS"

// ----------------------------------------------- //
// -------------- styled components -------------- //
// ----------------------------------------------- //

// -------------- Accordion -------------- //
const StyledAccordion = styled((props) => (
  <Accordion {...props} />
))({
  "&:not(:last-child)": {
    borderBottom: 0
  },
  "&:before": {
    display: "none"
  }
})

const StyledAccordionSummary = styled(
  (props) => <AccordionSummary {...props} />,
  {
    shouldForwardProp: (prop) => prop !== "packagingDisplay"
  }
)(({ packagingDisplay = false }) => ({
  flexDirection: "row-reverse",
  position: "relative",
  // opened and closed expanded icon
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded,& .MuiAccordionSummary-expandIconWrapper": {
    position: "sticky",
    left: PRODUCTION_STEPS_SPACINGS.ACCORDION_EXPANDED_ICON_LEFT,
    zIndex: 30
  },
  // row
  "& .MuiAccordionSummary-content": {
    padding: 0,
    margin: 0,
    height: 56,
    borderBottom: "1px solid #cccccc",
    marginLeft: -28, // important! for the summary to not take account of the expand icon space
    backgroundColor: packagingDisplay ? COLORS.BLUE_50 : COLORS.PRIMARY_BLUE_20
  }
}))

const Sections = ({
  recipe,
  sections,
  isEdition,
  onRowHover,
  onRowBlur,
  hoveredRow,
  onClearFocus,
  onFieldFocus,
  onFieldBlur,
  onKeyUp,
  deleteHover,
  setFieldValue,
  onDeleteBlur,
  errors,
  formValues,
  setValues,
  machineTypes,
  kitchenAreas,
  supplierItems,
  transformationModes,
  handleChange,
  allReusableSteps,
  stepNumbers,
  onDetachStep,
  onSectionNetWeightChange,
}) => {
  const [openOutputModalIndex, setOpenOutputModalIndex] = useState(-1) // index of the section for which the output modal is open
  const prevSectionsKeyRef = useRef(null)
  // sections form values computation
  const computeSectionsFormValues = useCallback((sections, sectionIndex) => {
    const newFormValues = { ...formValues }
    newFormValues.sections = sections

    if (!sections[sectionIndex] || !sections[sectionIndex].productionSteps) return
    sections[sectionIndex].productionSteps.forEach((step, stepIndex) => {
      step.stepComponents && step.stepComponents.forEach((_, ingredientIndex) => {
        computeProductionStepsRecipeOnFieldChange({
          recipe: newFormValues,
          sectionIndex,
          stepIndex,
          stepComponentIndex: ingredientIndex
        })
      })
    })
    computeRecipeData(newFormValues)

    setValues(newFormValues)
  }, [formValues, setValues])

  useEffect(() => {
    // ignore changes to packagingDisplay/deductFromSectionWeight (set by handleMaskSectionInPackaging) to avoid infinite loop
    const ignoredFields = ["packagingDisplay", "deductFromSectionWeight"]
    const key = JSON.stringify(sections.map((section) =>
      Object.fromEntries(Object.entries(section).filter(([k]) => !ignoredFields.includes(k)))
    ))
    if (key === prevSectionsKeyRef.current) return
    prevSectionsKeyRef.current = key

    // Recalculate recipe data to update section weights based on section output usage
    // This ensures "Poids en sortie" updates when section outputs are used/unused elsewhere
    if (recipe) {
      const updatedRecipe = { ...formValues }
      computeRecipeData(updatedRecipe)
      setValues(updatedRecipe)
    }
  }, [sections, isEdition]) //also trigger when isEdition changes to compute packagingDisplay value in preview when coming from edition

  // do not display sections row in preview if it's empty
  // display an empty row if sections is empty in edition mode
  // alway has a default section, see: getDefaultSection()
  if (!isEdition && !(sections.length && sections[0].id)) return

  const _isHover = (index) => {
    return (
      hoveredRow &&
      COMPONENT_NAME === hoveredRow.component &&
      hoveredRow.index === index
    )
  }

  const _isDeleteHover = (index) => {
    return (
      deleteHover &&
      COMPONENT_NAME === deleteHover.component &&
      deleteHover.index === index
    )
  }

  const _hasError = (index) => errors.sections && errors.sections[index] && (errors.sections[index].name || errors.sections[index].parentPercent)

  const _hasStepError = (index, field, sectionIndex) => {
    return errors.sections?.[sectionIndex]?.productionSteps?.[index]?.step?.[field]
  }

  const handleMaskSectionInPackaging = (index, options = {}) => {
    if (options.id) index = sections.findIndex(s => s.id === options.id)
    const value = options.value !== undefined ? options.value : sections[index].packagingDisplay
    const newSections = sections.map((section, sectionIndex) => {
      if (sectionIndex === index) {
        return {
          ...section,
          packagingDisplay: !value,
          deductFromSectionWeight: value
        }
      }
      return section
    })
    computeSectionsFormValues(newSections, index)
  }

  return (
    <Box className="flexColumn">
      {sections.map((section, index) => (
        <StyledAccordion
          elevation={0}
          defaultExpanded
          square
          disableGutters
          key={index}
        >
          <StyledAccordionSummary
            expandIcon={<img alt="chevron" src="/icons/chevron-down.svg" />}
            onMouseEnter={() => onRowHover(COMPONENT_NAME, index)}
            onMouseLeave={onRowBlur}
            packagingDisplay={section.packagingDisplay}
          >
            {isEdition ? (
              <EditableSection
                sections={sections}
                section={section}
                index={index}
                isHover={_isHover(index)}
                isDeleteHover={_isDeleteHover(index)}
                setFieldValue={setFieldValue}
                onFieldFocus={onFieldFocus}
                onFieldBlur={onFieldBlur}
                onKeyUp={onKeyUp}
                onDeleteBlur={onDeleteBlur}
                hasError={_hasError}
                computeSectionsFormValues={computeSectionsFormValues}
                onDivideSection={() => setOpenOutputModalIndex(index)}
                handleMaskSectionInPackaging={handleMaskSectionInPackaging}
                onSectionNetWeightChange={onSectionNetWeightChange}
              />
            ) : (
              <SectionPreview section={section} />
            )}
          </StyledAccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Steps
              recipe={recipe}
              section={section}
              steps={section.productionSteps}
              isEdition={isEdition}
              hoveredRow={hoveredRow}
              sectionIndex={index}
              onFieldFocus={onFieldFocus}
              onFieldBlur={onFieldBlur}
              onKeyUp={onKeyUp}
              onRowHover={onRowHover}
              onRowBlur={onRowBlur}
              errors={errors}
              machineTypes={machineTypes}
              kitchenAreas={kitchenAreas}
              supplierItems={supplierItems}
              transformationModes={transformationModes}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              hasError={_hasStepError}
              isReusable={false}
              onClearFocus={onClearFocus}
              formValues={formValues}
              setValues={setValues}
              allReusableSteps={allReusableSteps}
              stepNumbers={stepNumbers}
              onDetachStep={onDetachStep}
            />
          </AccordionDetails>
          <SectionOutput
            isEdition={isEdition}
            recipe={recipe}
            open={openOutputModalIndex === index}
            onClose={() => setOpenOutputModalIndex(-1)}
            onOpen={() => setOpenOutputModalIndex(index)}
            section={section}
            sectionIndex={index}
            setFieldValue={setFieldValue}
          />
        </StyledAccordion>
      ))}
    </Box>
  )
}

export default Sections
